import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { WIDGET_CATALOG } from "@/lib/widgetCatalog";
import { X, Info, AlignLeft, AlignCenter, AlignRight, Move, Type as TypeIcon } from "lucide-react";
import { clampLayout, minSizeFor } from "@/lib/layoutEngine";
import { Section, StyleToggle } from "@/components/bi/InspectorBits";
import { TextStyleEditor, RichTextSectionsEditor } from "@/components/bi/FormatEditors";

const NO_TYPE_SWITCH = ["text", "image", "multi_filter", "data_grid", "perspective"];

// Dynamic text values: {{name}} tokens resolved from live queries, with optional
// period-over-period comparison ({{name.delta_pct}} → "+12.4%").
function DynValuesEditor({ ins }) {
  const { widget, config, datasets, patch, patchConfig, patchOpts, numericFields = [], measureFields = [], fieldType } = ins;
  const vals = config.dynValues || [];
  const set = (arr) => patchConfig({ dynValues: arr });
  const upd = (i, p) => set(vals.map((v, j) => (j === i ? { ...v, ...p } : v)));
  const dateFields = measureFields.filter((f) => (fieldType ? fieldType(f.name) === "datetime" : /date|time/i.test(f.name)));
  return (
    <div data-testid="dynvalues-editor" className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] text-[color:var(--ink-2)] font-medium">Dynamic values (live data in your text)</Label>
        <button data-testid="dynvalues-add" disabled={!widget.dataset_id}
          onClick={() => set([...vals, { name: `value${vals.length + 1}`, field: numericFields[0]?.name || "", agg: "sum", period: "", dateField: "" }])}
          className="text-[11px] px-2 py-0.5 rounded-md border border-[color:var(--glass-border)] text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)] disabled:opacity-40">
          + Add value
        </button>
      </div>
      <div>
        <Label className="text-[10px] text-[color:var(--ink-3)]">Dataset</Label>
        <Select value={widget.dataset_id || ""} onValueChange={(v) => patch({ dataset_id: v })}>
          <SelectTrigger data-testid="dyntext-dataset" className="mt-0.5 w-full bg-[color:var(--surface-2)] h-8 text-xs"><SelectValue placeholder="Select dataset for live values" /></SelectTrigger>
          <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {vals.map((v, i) => (
        <div key={i} data-testid={`dynvalue-${i}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Input data-testid={`dynvalue-name-${i}`} value={v.name || ""} placeholder="token name"
              onChange={(e) => upd(i, { name: e.target.value.replace(/\W/g, "_") })}
              className="bg-[color:var(--surface)] h-7 text-xs flex-1" />
            <button onClick={() => set(vals.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Select value={v.field || ""} onValueChange={(x) => upd(i, { field: x })}>
              <SelectTrigger data-testid={`dynvalue-field-${i}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
              <SelectContent>{numericFields.map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={v.agg || "sum"} onValueChange={(x) => upd(i, { agg: x })}>
              <SelectTrigger data-testid={`dynvalue-agg-${i}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{["sum", "avg", "count", "count_distinct", "min", "max", "median"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Select value={v.period || "__none__"} onValueChange={(x) => upd(i, { period: x === "__none__" ? "" : x, dateField: v.dateField || dateFields[0]?.name || "" })}>
              <SelectTrigger data-testid={`dynvalue-period-${i}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Total (no comparison)</SelectItem>
                <SelectItem value="month">Compare by month</SelectItem>
                <SelectItem value="quarter">Compare by quarter</SelectItem>
                <SelectItem value="year">Compare by year</SelectItem>
              </SelectContent>
            </Select>
            {v.period && (
              <Select value={v.dateField || ""} onValueChange={(x) => upd(i, { dateField: x })}>
                <SelectTrigger data-testid={`dynvalue-datefield-${i}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Date field" /></SelectTrigger>
                <SelectContent>{(dateFields.length ? dateFields : measureFields).map((f) => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          {v.name && (
            <p className="text-[10px] text-[color:var(--ink-3)] leading-relaxed">
              {"Tokens: "}<code>{`{{${v.name}}}`}</code>
              {v.period ? <> · <code>{`{{${v.name}.prev}}`}</code> · <code>{`{{${v.name}.delta}}`}</code> · <code>{`{{${v.name}.delta_pct}}`}</code> · <code>{`{{${v.name}.period}}`}</code></> : null}
            </p>
          )}
        </div>
      ))}
      {!vals.length && <p className="text-[10px] text-[color:var(--ink-3)]">Example: pick sales · sum · compare by month, name it <code>{"sales"}</code>, then write “Sales grew {"{{sales.delta_pct}}"} vs last month”.</p>}
      {vals.some((v) => v.period) && (
        <label className="flex items-center justify-between gap-2 text-[11px] text-[color:var(--ink-2)]">
          <span>Color deltas (green up / red down)</span>
          <Switch data-testid="dynvalues-delta-colors" checked={(config.options?.deltaColors) !== false} onCheckedChange={(v) => patchOpts({ deltaColors: v })} />
        </label>
      )}
    </div>
  );
}

// Section 1/14 — General: title, subtitle, dataset & chart type (item 188/189).
export function InspectorGeneralSection({ ins }) {
  const { widget, config, opts, datasets, isText, patch, patchConfig, patchOpts } = ins;
  const isImageW = widget.type === "image";
  return (
    <Section value="general" icon={Info} title="General" testid="inspector-sec-general"
      hint="Name this widget and choose where its data comes from.">
      <div><Label className="text-xs text-[color:var(--ink-3)]">Title</Label><Input data-testid="inspector-title" value={widget.title || ""} onChange={(e) => patch({ title: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" placeholder="Widget title" /></div>
      {!isText && (
        <div><Label className="text-xs text-[color:var(--ink-3)]">Subtitle</Label><Input data-testid="inspector-subtitle" value={opts.subtitle || ""} onChange={(e) => patchOpts({ subtitle: e.target.value })} className="mt-1.5 bg-[color:var(--surface)]" placeholder="Optional subtitle" /></div>
      )}
      {!isText && (
        <div>
          <Label className="text-xs text-[color:var(--ink-3)]">Description</Label>
          <Textarea data-testid="inspector-description" value={opts.description || ""} onChange={(e) => patchOpts({ description: e.target.value })}
            className="mt-1.5 bg-[color:var(--surface)] min-h-[56px] text-xs" placeholder="Shown as an (i) icon beside the title — hover reveals this text" />
        </div>
      )}
      {isText && (
        <div className="flex items-center justify-between gap-2 py-1">
          <Label className="text-sm text-[color:var(--ink-2)]">Rich text editor</Label>
          <Switch data-testid="richtext-mode-toggle" checked={!!config.rich_enabled} onCheckedChange={(v) => patchConfig({ rich_enabled: v })} />
        </div>
      )}
      {isText && config.rich_enabled && (
        <p className="text-[10px] text-[color:var(--ink-3)]">Double-click the Title or Content on the canvas to edit with the floating formatting toolbar (fonts, colors, lists, alignment).</p>
      )}
      {isText && !config.rich_enabled && (
        <div><Label className="text-xs text-[color:var(--ink-3)]">Content</Label><Textarea data-testid="text-content" value={config.text || ""} onChange={(e) => patchConfig({ text: e.target.value })} className="mt-1.5 bg-[color:var(--surface)] min-h-[140px]" placeholder={"Write a note… Supports # headings, **bold**, *italic*, - bullets and {{value}} tokens"} /></div>
      )}
      {isText && !config.rich_enabled && <DynValuesEditor ins={ins} />}
      {!isText && !isImageW && (
        <div>
          <Label className="text-xs text-[color:var(--ink-3)]">Dataset</Label>
          <Select value={widget.dataset_id || ""} onValueChange={(v) => patch({ dataset_id: v })}>
            <SelectTrigger data-testid="inspector-dataset" className="mt-1.5 w-full bg-[color:var(--surface)]"><SelectValue placeholder="Select dataset" /></SelectTrigger>
            <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {!isText && !NO_TYPE_SWITCH.includes(widget.type) && (
        <div>
          <Label className="text-xs text-[color:var(--ink-3)]">Chart type</Label>
          <Select value={widget.type} onValueChange={(v) => patch({ type: v })}>
            <SelectTrigger data-testid="inspector-charttype" className="mt-1.5 w-full bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
            <SelectContent>{WIDGET_CATALOG.filter((w) => !["text", "image", "multi_filter", "data_grid"].includes(w.type)).map((w) => <SelectItem key={w.type} value={w.type}>{w.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
    </Section>
  );
}

// Section 2/14 — Layout: position, size, lock, mobile visibility (item 188/191).
export function InspectorLayoutSection({ ins }) {
  const { widget, patch, cols = 12 } = ins;
  // Children of a Combine container are positioned in the container's own 12-col grid.
  const inContainer = !!widget.parent_id;
  const key = inContainer ? "layout_child" : "layout";
  const gridCols = inContainer ? 12 : cols;
  const L = widget[key] || {};
  const min = minSizeFor(widget.type);
  const setL = (k, v) => patch({ [key]: clampLayout({ ...L, [k]: Number(v) }, widget.type, gridCols) });
  const cell = (label, k, testid) => (
    <div className="flex-1 min-w-0">
      <Label className="text-[10px] text-[color:var(--ink-3)]">{label}</Label>
      <Input data-testid={testid} type="number" min={k === "w" ? min.w : k === "h" ? min.h : 0} max={k === "x" ? gridCols - (L.w || 1) : k === "w" ? gridCols : undefined}
        value={L[k] ?? 0} onChange={(e) => setL(k, e.target.value)} className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs" />
    </div>
  );
  return (
    <Section value="layout" icon={Move} title="Layout" testid="inspector-sec-layout"
      hint={inContainer ? `Position & size inside the container (12-column grid). Min ${min.w}×${min.h}.` : `Grid position & size in canvas units (${gridCols} columns). Min ${min.w}×${min.h}. Lock to prevent accidental moves.`}>
      <div className="flex gap-1.5">{cell("X", "x", "layout-x")}{cell("Y", "y", "layout-y")}{cell("W", "w", "layout-w")}{cell("H", "h", "layout-h")}</div>
      <StyleToggle label="Lock position & size" testid="layout-lock" checked={!!widget.locked} onChange={(v) => patch({ locked: v })} />
      <StyleToggle label="Hide on mobile" testid="layout-hide-mobile" checked={!!widget.hidden_mobile} onChange={(v) => patch({ hidden_mobile: v })} />
    </Section>
  );
}

// Section 3/14 — Typography: title alignment/size/color, header visibility (item 188).
const FONT_FAMILIES = [
  { value: "default", label: "Theme default" },
  { value: "Outfit, sans-serif", label: "Outfit" },
  { value: "Manrope, sans-serif", label: "Manrope" },
  { value: "Inter, sans-serif", label: "Inter" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Times New Roman', serif", label: "Times" },
  { value: "'Courier New', monospace", label: "Monospace" },
  { value: "Arial, sans-serif", label: "Arial" },
];
const FONT_WEIGHTS = [
  { value: "default", label: "Auto" },
  { value: "normal", label: "Regular" },
  { value: "medium", label: "Medium" },
  { value: "semibold", label: "Semibold" },
  { value: "bold", label: "Bold" },
];
export function InspectorTypographySection({ ins }) {
  const { widget, config, opts, isText, patchOpts, patchConfig } = ins;
  const ts = opts.titleStyle || {};
  const ss = opts.subtitleStyle || {};
  const setTs = (patch) => patchOpts({ titleStyle: { ...ts, ...patch } });
  const setSs = (patch) => patchOpts({ subtitleStyle: { ...ss, ...patch } });
  return (
    <Section value="typography" icon={TypeIcon} title="Typography" testid="inspector-sec-typography"
      hint="Title & subtitle formatting — font, size, weight, color, alignment.">
      <>
        <div>
            <Label className="text-xs text-[color:var(--ink-3)]">Title format</Label>
            <div className="flex items-center gap-1.5 mt-1.5">
              <div className="flex items-center rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-0.5">
                {[{ v: "left", I: AlignLeft }, { v: "center", I: AlignCenter }, { v: "right", I: AlignRight }].map(({ v, I }) => (
                  <button key={v} data-testid={`title-align-${v}`} title={`Align ${v}`} onClick={() => setTs({ align: v })}
                    className={"p-1.5 rounded-md transition-colors " + ((ts.align || "left") === v ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>
                    <I className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
              <Select value={ts.size || "md"} onValueChange={(v) => setTs({ size: v })}>
                <SelectTrigger data-testid="title-size" className="bg-[color:var(--surface)] w-[92px] h-8 text-xs shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sm">Small</SelectItem>
                  <SelectItem value="md">Medium</SelectItem>
                  <SelectItem value="lg">Large</SelectItem>
                </SelectContent>
              </Select>
              <input data-testid="title-color" type="color" title="Title color" value={ts.color || "#3d4966"}
                onChange={(e) => setTs({ color: e.target.value })}
                className="h-8 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
              {ts.color && (
                <button title="Reset color" onClick={() => { const { color, ...rest } = ts; patchOpts({ titleStyle: rest }); }} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Font family</Label>
              <Select value={ts.fontFamily || "default"} onValueChange={(v) => setTs({ fontFamily: v === "default" ? "" : v })}>
                <SelectTrigger data-testid="title-font-family" className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Custom size (px)</Label>
              <Input data-testid="title-size-px" type="number" min={8} max={64} value={ts.sizePx ?? ""} placeholder="Preset"
                onChange={(e) => setTs({ sizePx: e.target.value === "" ? "" : Math.max(8, Math.min(64, Number(e.target.value))) })}
                className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Weight</Label>
              <Select value={ts.weight || "default"} onValueChange={(v) => setTs({ weight: v === "default" ? "" : v })}>
                <SelectTrigger data-testid="title-weight" className="mt-0.5 bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{FONT_WEIGHTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Header background</Label>
              <div className="mt-0.5 flex items-center gap-1">
                <input data-testid="title-header-bg" type="color" value={ts.bg || "#ffffff"} onChange={(e) => setTs({ bg: e.target.value })}
                  className="h-8 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                {ts.bg && <button onClick={() => { const { bg, ...rest } = ts; patchOpts({ titleStyle: rest }); }} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3 h-3" /></button>}
              </div>
            </div>
          </div>
          <StyleToggle label="Bold title" testid="title-bold" checked={!!ts.bold} onChange={(v) => setTs({ bold: v })} />
          <StyleToggle label="Italic title" testid="title-italic" checked={!!ts.italic} onChange={(v) => setTs({ italic: v })} />
          <StyleToggle label="Underline title" testid="title-underline" checked={!!ts.underline} onChange={(v) => setTs({ underline: v })} />
          {opts.subtitle && (
            <div className="pt-1 border-t border-[color:var(--glass-border)]">
              <Label className="text-xs text-[color:var(--ink-3)]">Subtitle format</Label>
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                <Input data-testid="subtitle-size-px" type="number" min={8} max={40} value={ss.sizePx ?? ""} placeholder="Size px"
                  onChange={(e) => setSs({ sizePx: e.target.value === "" ? "" : Math.max(8, Math.min(40, Number(e.target.value))) })}
                  className="bg-[color:var(--surface)] h-8 text-xs" />
                <input data-testid="subtitle-color" type="color" title="Subtitle color" value={ss.color || "#7c8698"}
                  onChange={(e) => setSs({ color: e.target.value })}
                  className="h-8 w-full rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                <Select value={ss.fontFamily || "default"} onValueChange={(v) => setSs({ fontFamily: v === "default" ? "" : v })}>
                  <SelectTrigger data-testid="subtitle-font-family" className="bg-[color:var(--surface)] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <StyleToggle label="Italic subtitle" testid="subtitle-italic" checked={!!ss.italic} onChange={(v) => setSs({ italic: v })} />
            </div>
          )}
          <StyleToggle label="Hide header" testid="title-hide-header" checked={!!opts.hideHeader} onChange={(v) => patchOpts({ hideHeader: v })} />
        </>
      {isText && !config.rich_enabled && <TextStyleEditor config={config} patchConfig={patchConfig} />}
      {isText && config.rich_enabled && <RichTextSectionsEditor config={config} patchConfig={patchConfig} />}
    </Section>
  );
}
